import { useTranslation } from 'react-i18next'
import { useMineMarkedsomrader } from '../../hooks/useMineMarkedsomrader'
import { useActiveMarkedsomrade } from '../../hooks/useActiveMarkedsomrade'
import type { BruktbilfinansieringFilters } from './hooks/useBruktbilfinansiering'

type TabContext = 'oversikt' | 'forhandlere' | 'datakvalitet' | 'klasser' | 'lanebelop-profil' | 'alder-profil' | 'bilalder' | 'bankmarked'

interface Props {
  bankName: string
  filters: BruktbilfinansieringFilters
  tab: TabContext
  markedsomradeName?: string | null
  overrideClasses?: string[]
}

const LAG_DAYS = 15

function dataCutoff(): string {
  const d = new Date()
  d.setDate(d.getDate() - LAG_DAYS)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const TAB_DESCRIPTION_KEYS = {
  oversikt: 'infoBox.tabs.oversikt',
  forhandlere: 'infoBox.tabs.forhandlere',
  datakvalitet: 'infoBox.tabs.datakvalitet',
  klasser: 'infoBox.tabs.klasser',
  'lanebelop-profil': 'infoBox.tabs.lanebelopProfil',
  'alder-profil': 'infoBox.tabs.alderProfil',
  bilalder: 'infoBox.tabs.bilalder',
  bankmarked: 'infoBox.tabs.bankmarked',
} as const satisfies Record<TabContext, string>

const DIRECTION_KEYS = {
  'bilkjøper': 'directions.bilkjoper',
  bilselger: 'directions.bilselger',
  begge: 'directions.begge',
} as const

function varebilLabelKey(varebil: boolean | undefined) {
  if (varebil === true) return 'shared.vehicle.varebil' as const
  if (varebil === false) return 'shared.vehicle.personbil' as const
  return 'shared.vehicle.alle' as const
}

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

export default function BruktbilfinansieringInfoBox({ bankName, filters, tab, markedsomradeName: markedsomradeNameProp, overrideClasses }: Props) {
  const { t } = useTranslation('bbf')
  const cutoff = dataCutoff()

  // Derive markedsomrade name from hooks if not passed as prop
  const { items: omrader } = useMineMarkedsomrader()
  const { activeId } = useActiveMarkedsomrade()
  const activeOmrade = activeId !== null ? omrader.find(o => o.id === activeId) : undefined
  const geoFilterActive = !!(filters.buyerPostnummer || filters.dealerPostnummer)
  const markedsomradeName = geoFilterActive ? (markedsomradeNameProp ?? activeOmrade?.name ?? null) : null

  // Use tab-local class selection when provided, else fall back to global filter
  const effectiveClasses = overrideClasses ?? filters.classes ?? []

  // Derive unique buyer types from classes filter
  const buyerTypes = [...new Set(
    effectiveClasses
      .map(c => {
        if (c.endsWith('_TO_PERSON')) return 'buyerTypes.PERSON' as const
        if (c.endsWith('_TO_FIRMA')) return 'buyerTypes.FIRMA' as const
        if (c.endsWith('_TO_BILUTLEIE')) return 'buyerTypes.BILUTLEIE' as const
        return null
      })
      .filter(k => k !== null)
  )].map(k => t(k))

  return (
    <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
      <p className="mb-1">{t(TAB_DESCRIPTION_KEYS[tab])}</p>
      <p className="text-xs text-blue-600 dark:text-blue-400">
        <span className="font-medium">{bankName}</span>
        {' · '}
        {fmtDate(filters.startDate)} – {fmtDate(filters.endDate)}
        {' · '}
        {t(varebilLabelKey(filters.varebil))}
        {markedsomradeName && ` · ${t('shared.markedsomrade', { name: markedsomradeName })}${filters.postnummerDirection ? ` (${t(DIRECTION_KEYS[filters.postnummerDirection])})` : ''}`}
        {buyerTypes.length === 3 && ` · ${t('infoBox.typeAll')}`}
        {buyerTypes.length > 0 && buyerTypes.length < 3 && ` · ${t('infoBox.typeList', { types: buyerTypes.join(', ') })}`}
        {overrideClasses && ` · ${t('infoBox.classesSelected', { n: overrideClasses.length })}`}
      </p>
      {filters.endDate > cutoff && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
          {t('infoBox.lagWarning', { endDate: fmtDate(filters.endDate), days: LAG_DAYS, cutoff: fmtDate(cutoff) })}
        </p>
      )}
    </div>
  )
}
